/* eslint-disable i18next/no-literal-string */

import { keyframes } from "@emotion/react";
import {
  Alert,
  Box,
  Button,
  Icon,
  IconButton,
  Image,
  OsGrid,
  OsModal,
  OsTextField,
  Paper,
  Tab,
  Tabs,
  Theme,
  Typography,
} from "@outschool/backpack";
import { borderRadius } from "@outschool/backpack/dist-cjs/theme/borderRadius";
import {
  faBookOpenRegular,
  faCheck,
  faChevronLeft,
  faPencil,
  faRepeat,
  faSparkles,
  faUser,
} from "@outschool/icons";
import { captureError } from "@outschool/platform";
import { TrackedButton, useFeatureFlag } from "@outschool/ui-components-shared";
import { Loading, LoadingIcon } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import { navigate } from "@patched/hookrouter";
import React, { useCallback, useEffect, useRef, useState } from "react";

import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import {
  getCurrentLearner,
  useCurrentLearner,
} from "../../providers/CurrentLearnerProvider";
import useLoadingButtonText from "../LearnerPlay/useLoadingButtonText";
import { AvatarBuilderArtStyleTab } from "./AvatarBuilderArtStyleTab";
import { AvatarBuilderBackgroundTab } from "./AvatarBuilderBackgroundTab";
import { AvatarBuilderGenerationDisabled } from "./AvatarBuilderGenerationDisabled";
import { AvatarBuilderHeroLayout } from "./AvatarBuilderHeroLayout";
import { AvatarBuilderTypeTab } from "./AvatarBuilderTypeTab";
import {
  POLLING_INTERVAL_MS,
  useCheckAvatarGenerationQuery,
  useClearAvatarsMutation,
  useSaveAvatarMutation,
  useStartAvatarGenerationMutation,
} from "./hooks";
import { RemoveCreatureButton } from "./RemoveCreatureButton";

const MAX_WAIT_TIME_MS = 60000;

const LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

type TabId = "type" | "artStyle" | "background";

export default function AvatarBuilder() {
  useAnalyticsPage({ pageName: PageType.AvatarBuilder });
  const currentLearner = useCurrentLearner();
  const isGenerationDisabled = useFeatureFlag(
    "disable-learner-avatar-generation",
  );
  const isMobile = useIsMobile();

  // Customization state
  const [activeTab, setActiveTab] = useState<TabId>("type");
  const [creatureType, setCreatureType] = useState<string>("human");
  const [artStyle, setArtStyle] = useState<string>("fantastical");
  const [background, setBackground] = useState<string>("forest");
  const [gender, setGender] = useState<string>("none");
  const [ageRange, setAgeRange] = useState<string>("");
  const [hairColor, setHairColor] = useState<string>("");
  const [skinColor, setSkinColor] = useState<string>("");

  // Generation state
  const [generatedAvatar, setGeneratedAvatar] = useState<{
    name: string;
    description: string;
    imageUrl: string;
  } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<Error | null>(null);

  const [avatarDescription, setAvatarDescription] = useState<string | null>(
    null,
  );
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  const [isEditingAvatarName, setIsEditingAvatarName] = useState(false);
  const [draftAvatarName, setDraftAvatarName] = useState("");
  const avatarNameInputRef = useRef<HTMLInputElement | null>(null);

  // Save state
  const [isSaved, setIsSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveAvatar, { loading: isSaving }] = useSaveAvatarMutation();

  const [clearAvatars, { loading: isClearing }] = useClearAvatarsMutation();

  const avatarInfoRef = useRef<{
    name: string;
    description: string;
    creatureType: string;
    generationId: string;
  } | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [startAvatarGeneration] = useStartAvatarGenerationMutation();
  const [checkAvatarGeneration] = useCheckAvatarGenerationQuery();

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isEditingAvatarName) {
      const input = avatarNameInputRef.current;
      input?.focus();
      input?.select();
    }
  }, [isEditingAvatarName]);

  const pollForCompletion = useCallback(async () => {
    if (!avatarInfoRef.current) {
      return;
    }

    const {
      name,
      description,
      creatureType: ct,
      generationId,
    } = avatarInfoRef.current;
    const isTimedOut =
      pollingStartTimeRef.current &&
      Date.now() - pollingStartTimeRef.current > MAX_WAIT_TIME_MS;

    const finishPolling = (error?: Error, imageUrl?: string) => {
      setIsGenerating(false);
      avatarInfoRef.current = null;
      pollingStartTimeRef.current = null;

      if (error) {
        console.error("Avatar generation error:", error);
        captureError(error, { extra: { creatureType: ct } });
        setGenerateError(error);
      } else if (imageUrl) {
        setGeneratedAvatar({ name, description, imageUrl });
        setIsEditingAvatarName(false);
        setIsSaved(false);
        setSaveError(null);
      }
    };

    if (isTimedOut) {
      return finishPolling(new Error("Avatar generation timed out"));
    }

    try {
      const result = await checkAvatarGeneration({
        variables: {
          generationId,
          avatarName: name,
          creatureType: ct,
        },
      });

      const status = result.data?.checkAvatarGeneration?.status;
      const imageUrl = result.data?.checkAvatarGeneration?.imageUrl;

      if (status === "pending") {
        pollingTimeoutRef.current = setTimeout(
          pollForCompletion,
          POLLING_INTERVAL_MS,
        );
      } else if (status === "complete" && imageUrl) {
        finishPolling(undefined, imageUrl);
      } else if (status === "failed") {
        finishPolling(new Error("Avatar generation failed"));
      } else {
        finishPolling(
          new Error("Avatar generation completed but no image URL returned"),
        );
      }
    } catch (error) {
      finishPolling(error as Error);
    }
  }, [checkAvatarGeneration]);

  const loadingButtonText = useLoadingButtonText(isGenerating);

  const handleGenerate = async () => {
    if (!creatureType) {
      return;
    }

    setGenerateError(null);
    setIsGenerating(true);
    setAvatarDescription(null);

    try {
      const result = await startAvatarGeneration({
        variables: {
          creatureType,
          style: artStyle,
          background,
          gender:
            creatureType === "human" && gender !== "none" ? gender : undefined,
          ageRange: creatureType === "human" && ageRange ? ageRange : undefined,
          hairColor:
            creatureType === "human" && hairColor ? hairColor : undefined,
          skinColor:
            creatureType === "human" && skinColor ? skinColor : undefined,
        },
      });

      const startResult = result.data?.startAvatarGeneration;
      if (!startResult?.generationId) {
        throw new Error("No generation ID returned");
      }

      if (startResult.description?.trim()) {
        setAvatarDescription(startResult.description);
      }

      avatarInfoRef.current = {
        name: startResult.name,
        description: startResult.description,
        creatureType,
        generationId: startResult.generationId,
      };

      pollingStartTimeRef.current = Date.now();
      pollingTimeoutRef.current = setTimeout(
        pollForCompletion,
        POLLING_INTERVAL_MS,
      );
    } catch (error) {
      captureError(error as Error, {
        extra: { creatureType },
      });
      setGenerateError(error as Error);
      setIsGenerating(false);
    }
  };

  const handleStartOver = () => {
    setCreatureType("monster");
    setArtStyle("fantastical");
    setBackground("forest");
    setGender("none");
    setAgeRange("");
    setHairColor("");
    setSkinColor("");
    setGeneratedAvatar(null);
    setAvatarDescription(null);
    setIsDescriptionModalOpen(false);
    setGenerateError(null);
    setIsSaved(false);
    setSaveError(null);
    setActiveTab("type");
    setIsEditingAvatarName(false);
    setDraftAvatarName("");
  };

  const handleSaveAsAvatar = async () => {
    if (!generatedAvatar) {
      return;
    }
    setSaveError(null);
    try {
      await saveAvatar({
        variables: {
          imageCdnUrl: generatedAvatar.imageUrl,
          name: generatedAvatar.name,
          description: generatedAvatar.description,
          creatureType,
        },
        refetchQueries: [getCurrentLearner],
      });
      setIsSaved(true);
    } catch (err: any) {
      setSaveError(err.message || "Failed to save avatar");
    }
  };

  if (!currentLearner) {
    return <Loading />;
  }

  const handleBack = () => {
    if (currentLearner) {
      navigate(`/profile/${currentLearner.uid}`);
    } else {
      navigate("/");
    }
  };

  const avatarDescriptionText = (
    generatedAvatar?.description ??
    avatarDescription ??
    ""
  ).trim();

  if (isGenerationDisabled) {
    return (
      <AvatarBuilderGenerationDisabled
        isMobile={isMobile}
        onBack={handleBack}
        isClearing={isClearing}
        onClearCreature={async () => {
          await clearAvatars({
            refetchQueries: [getCurrentLearner],
          });
        }}
      />
    );
  }

  // --- Main side-by-side layout ---

  return (
    <AvatarBuilderHeroLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <OsModal
          open={isDescriptionModalOpen}
          onClose={() => setIsDescriptionModalOpen(false)}
          title="Description"
          modalDesktopWidth={480}
          actionPrimary={
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsDescriptionModalOpen(false)}
            >
              Close
            </Button>
          }
        >
          <Typography
            variant="body1"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {avatarDescriptionText}
          </Typography>
        </OsModal>
        <Button
          startIcon={<Icon icon={faChevronLeft} />}
          size="small"
          sx={{
            width: "fit-content",
            marginBottom: "5px",
            marginLeft: { xs: "5px", md: "20px" },
          }}
          onClick={handleBack}
        >
          Back
        </Button>

        <OsGrid
          sx={{ padding: { xs: "0px", md: "0px 20px" } }}
          container
          spacing={3}
        >
          {/* Left Panel: Customize */}
          <OsGrid xs={12} md={6}>
            <Paper
              variant="elevation"
              sx={{
                borderRadius: borderRadius.medium,
                padding: { xs: "16px", md: "24px" },
                display: "flex",
                flexDirection: "column",
                height: "100%",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  pb: "57px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  minHeight: 0,
                  maxHeight: { md: "calc(100vh - 200px)" },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, flexShrink: 0 }}
                >
                  1. Customize
                </Typography>

                <Tabs
                  value={activeTab}
                  onChange={(_event, value) => setActiveTab(value as TabId)}
                  sx={{ marginBottom: 10, flexShrink: 0 }}
                >
                  <Tab value="type" label="Type" />
                  <Tab value="artStyle" label="Art Style" />
                  <Tab value="background" label="Background" />
                </Tabs>

                {/* Tab content — only this region scrolls */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    mt: 2,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: "40px",
                      pointerEvents: "none",
                      background:
                        "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(0,0,0,0.08) 100%)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      paddingBottom: "25px",
                    }}
                  >
                    {activeTab === "type" && (
                      <AvatarBuilderTypeTab
                        creatureType={creatureType}
                        setCreatureType={setCreatureType}
                        gender={gender}
                        setGender={setGender}
                        ageRange={ageRange}
                        setAgeRange={setAgeRange}
                        hairColor={hairColor}
                        setHairColor={setHairColor}
                        skinColor={skinColor}
                        setSkinColor={setSkinColor}
                      />
                    )}
                    {activeTab === "artStyle" && (
                      <AvatarBuilderArtStyleTab
                        artStyle={artStyle}
                        setArtStyle={setArtStyle}
                      />
                    )}
                    {activeTab === "background" && (
                      <AvatarBuilderBackgroundTab
                        background={background}
                        setBackground={setBackground}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Bottom sticky CTA */}
              <Box
                sx={(theme: Theme) => ({
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: { xs: "12px 16px", md: "16px 24px" },
                  borderTop: `1px solid ${theme.palette.grey[200]}`,
                  backgroundColor: theme.palette.common.white,
                  borderRadius: `0 0 ${borderRadius.medium}px ${borderRadius.medium}px`,
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                })}
              >
                {isGenerating ? (
                  <Button
                    startIcon={<LoadingIcon />}
                    variant="contained"
                    color="primary"
                    disabled={true}
                    fullWidth
                    sx={(theme: Theme) => ({
                      "&.Mui-disabled": {
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                        backgroundSize: "400% 100%",
                        animation: `${LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION} 2s ease infinite`,
                        color: theme.palette.common.white,
                      },
                    })}
                  >
                    {loadingButtonText}
                  </Button>
                ) : (
                  <TrackedButton
                    startIcon={<Icon icon={faSparkles} />}
                    variant="contained"
                    color="primary"
                    onClick={handleGenerate}
                    disabled={!creatureType}
                    fullWidth
                    trackingName="avatar_builder.make_new_avatar_button_clicked"
                    trackingSharedProperties={{
                      creatureType,
                      artStyle,
                      background,
                    }}
                  >
                    Create Avatar
                  </TrackedButton>
                )}
                <Button
                  startIcon={<Icon icon={faRepeat} />}
                  variant="outlined"
                  onClick={handleStartOver}
                  disabled={isGenerating}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0, marginLeft: 10 }}
                >
                  Start Over
                </Button>
              </Box>
            </Paper>
          </OsGrid>

          {/* Right Panel: My Avatar */}
          <OsGrid xs={12} md={6}>
            <Paper
              variant="elevation"
              sx={{
                borderRadius: borderRadius.medium,
                padding: { xs: "16px", md: "24px" },
                minHeight: { md: "500px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {generatedAvatar && isEditingAvatarName ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{ fontWeight: 600, flexShrink: 0 }}
                    >
                      2.
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <OsTextField
                        ariaLabel="Avatar name"
                        value={draftAvatarName}
                        onChange={e => setDraftAvatarName(e.target.value)}
                        fullWidth
                        size="small"
                        inputRef={avatarNameInputRef}
                        InputProps={{
                          sx: {
                            "& input": {
                              fontWeight: 600,
                              typography: "h6",
                            },
                          },
                        }}
                      />
                    </Box>
                    <IconButton
                      icon={faCheck}
                      aria-label="Confirm avatar name"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setGeneratedAvatar(prev => {
                          if (!prev) {
                            return prev;
                          }
                          const trimmed = draftAvatarName.trim();
                          return {
                            ...prev,
                            name: trimmed || prev.name,
                          };
                        });
                        setIsEditingAvatarName(false);
                      }}
                      sx={{
                        flexShrink: 0,
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      2. {generatedAvatar?.name ?? "My avatar"}
                    </Typography>
                    {generatedAvatar ? (
                      <IconButton
                        icon={faPencil}
                        aria-label="Edit avatar name"
                        variant="text"
                        color="neutral"
                        size="small"
                        onClick={() => {
                          setDraftAvatarName(generatedAvatar.name);
                          setIsEditingAvatarName(true);
                        }}
                      />
                    ) : null}
                  </Box>
                )}

                {avatarDescriptionText ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Icon icon={faBookOpenRegular} />}
                    onClick={() => setIsDescriptionModalOpen(true)}
                    sx={{
                      alignSelf: "flex-start",
                      borderRadius: 9999,
                      textTransform: "none",
                      marginTop: 5,
                      marginBottom: 5,
                    }}
                  >
                    View Description
                  </Button>
                ) : null}

                {generateError && (
                  <Alert
                    severity="error"
                    title="Oops, looks like the Avatar Wizard accidentally put in the wrong ingredients. Try again!"
                  />
                )}
                {saveError && <Alert severity="error" title={saveError} />}
                {isSaved && (
                  <Alert
                    severity="success"
                    title="Avatar saved as your profile picture!"
                  />
                )}

                {/* Preview area */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 300,
                  }}
                >
                  {generatedAvatar ? (
                    <Image
                      src={generatedAvatar.imageUrl}
                      alt={generatedAvatar.name}
                      width="100%"
                      height="auto"
                      sx={{
                        borderRadius: borderRadius.medium,
                        objectFit: "contain",
                        maxWidth: 450,
                        maxHeight: 450,
                      }}
                    />
                  ) : isGenerating ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <LoadingIcon />
                      <Typography variant="body1" color="textSecondary">
                        Creating your avatar...
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={(theme: Theme) => ({
                        padding: 4,
                        borderRadius: borderRadius.medium,
                        backgroundColor: theme.palette.grey[50],
                        width: "100%",
                        minHeight: 250,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      })}
                    >
                      <Icon
                        icon={faSparkles}
                        sx={(theme: Theme) => ({
                          fontSize: "3rem",
                          color: theme.palette.primary.main,
                          opacity: 0.6,
                        })}
                      />
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        sx={{ textAlign: "center" }}
                      >
                        Pick options on the left, then press
                        <br />
                        &quot;Make New Avatar&quot;
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Action buttons under preview */}
                {generatedAvatar && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <TrackedButton
                      variant="contained"
                      color="primary"
                      onClick={handleSaveAsAvatar}
                      disabled={isSaving || isSaved}
                      startIcon={<Icon icon={faUser} />}
                      trackingName="avatar_builder.use_as_avatar_button_clicked"
                      trackingSharedProperties={{
                        creatureType,
                      }}
                      sx={{ marginTop: 5, marginBottom: 5 }}
                    >
                      {isSaving
                        ? "Saving..."
                        : isSaved
                        ? "Saved!"
                        : "Use As My Avatar"}
                    </TrackedButton>
                  </Box>
                )}

                {/* Remove creature */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.5,
                    mt: "auto",
                    pt: 2,
                  }}
                >
                  <RemoveCreatureButton
                    isClearing={isClearing}
                    isGenerating={isGenerating}
                    onClear={async () => {
                      await clearAvatars({
                        refetchQueries: [getCurrentLearner],
                      });
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Removing your creature will reset your profile picture to
                    the default avatar
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </OsGrid>
        </OsGrid>
      </Box>
    </AvatarBuilderHeroLayout>
  );
}
